import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDuration, CATEGORY_NAME, BACKEND_URL } from "@/lib/api";
import { Eye, Heart, MessageSquare, FolderHeart, Film, TrendingUp, ArrowRight, Sparkles, Download, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";

export default function ClientDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/client/stats")
      .then((r) => setStats(r.data))
      .catch(() => toast.error("Failed to load stats"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-12 text-stone-500">Loading…</div>;
  if (!stats) return null;

  const { totals, top_clips, recent_comments, reels = [] } = stats;

  return (
    <div data-testid="client-dashboard">
      <div className="border-l-4 border-orange-600 pl-5 mb-8">
        <div className="label-tiny mb-2">Your Stats</div>
        <h1 className="font-display uppercase text-5xl font-bold leading-[0.95]">Engagement</h1>
        <p className="text-stone-400 mt-3">Track how your runs are performing across every gallery.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-10">
        <StatCard icon={FolderHeart} label="Galleries" value={totals.galleries} />
        <StatCard icon={Film} label="Clips" value={totals.clips} />
        <StatCard icon={Sparkles} label="Your Reels" value={totals.reels || 0} />
        <StatCard icon={Eye} label="Clip Views" value={totals.views} accent />
        <StatCard icon={Eye} label="Reel Views" value={totals.reel_views || 0} accent />
        <StatCard icon={Heart} label="Reactions" value={totals.reactions} accent />
        <StatCard icon={MessageSquare} label="Comments" value={totals.comments} accent />
      </div>

      <section className="mb-10">
        <h2 className="font-display uppercase text-2xl font-bold mb-4 inline-flex items-center gap-2">
          <TrendingUp size={20} /> Top Runs
        </h2>
        {top_clips.length === 0 ? (
          <div className="border border-dashed border-stone-800 p-8 text-center text-stone-500 text-sm">
            No views yet — share your clips on social to drive engagement.
          </div>
        ) : (
          <div className="border border-stone-800" data-testid="top-clips-table">
            <div className="hidden md:grid grid-cols-[80px_2fr_100px_80px_80px_80px] gap-3 px-4 py-2 border-b border-stone-800 text-[10px] uppercase tracking-widest text-stone-500">
              <div></div><div>Clip</div><div>Category</div>
              <div className="text-right">Views</div>
              <div className="text-right">Reactions</div>
              <div className="text-right">Comments</div>
            </div>
            {top_clips.map((c) => (
              <div key={c.id} className="grid grid-cols-[80px_2fr_100px_80px_80px_80px] gap-3 px-4 py-3 border-b border-stone-900 items-center" data-testid={`top-clip-${c.id}`}>
                <div className="aspect-video bg-black overflow-hidden">
                  <img
                    src={`${BACKEND_URL}/api/clips/${c.id}/thumbnail`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    alt={c.name}
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                </div>
                <div className="min-w-0">
                  <div className="font-display uppercase font-semibold text-sm truncate">{c.name}</div>
                  {c.sub_label && c.name !== c.sub_label && (
                    <div className="text-[11px] uppercase tracking-wider text-orange-400/80 truncate">{c.sub_label}</div>
                  )}
                  <div className="text-[10px] font-mono-tc text-stone-500">{fmtDuration(c.duration)}</div>
                </div>
                <div className="text-xs text-stone-400">{CATEGORY_NAME[c.category] || c.category}</div>
                <div className="text-right font-mono-tc text-orange-400">{c.views}</div>
                <div className="text-right font-mono-tc text-stone-300">{c.reactions}</div>
                <div className="text-right font-mono-tc text-stone-300">{c.comments}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mb-10" data-testid="dashboard-reels-section">
        <h2 className="font-display uppercase text-2xl font-bold mb-4 inline-flex items-center gap-2">
          <Sparkles size={18} /> Your Highlight Reels
        </h2>
        {reels.length === 0 ? (
          <div className="border border-dashed border-stone-800 p-8 text-center text-stone-500 text-sm">
            You haven't built any reels yet. Open a gallery and tap <span className="text-orange-400">Build a Highlight Reel</span>.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="dashboard-reels-grid">
            {reels.map((r) => <DashboardReelCard key={r.id} reel={r} />)}
          </div>
        )}
      </section>

      <section>
        <h2 className="font-display uppercase text-2xl font-bold mb-4 inline-flex items-center gap-2">
          <MessageSquare size={18} /> Latest Comments
        </h2>
        {recent_comments.length === 0 ? (
          <div className="border border-dashed border-stone-800 p-8 text-center text-stone-500 text-sm">
            No comments yet.
          </div>
        ) : (
          <div className="space-y-2" data-testid="recent-comments">
            {recent_comments.map((cm, i) => (
              <div key={i} className="border border-stone-800 p-3" data-testid={`comment-${i}`}>
                <div className="flex items-baseline justify-between mb-1">
                  <div className="text-xs uppercase tracking-widest text-orange-400/80">{cm.author}</div>
                  <div className="text-[10px] text-stone-600">{(cm.created_at || "").slice(0, 10)}</div>
                </div>
                <div className="text-sm text-stone-300">{cm.text}</div>
                <div className="text-[10px] text-stone-500 mt-1.5">on <span className="text-stone-300">{cm.clip_name}</span></div>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="mt-10">
        <Link to="/portal" className="text-stone-400 hover:text-orange-400 text-xs uppercase tracking-widest inline-flex items-center gap-1">
          Back to galleries <ArrowRight size={11} />
        </Link>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className={`border p-4 ${accent ? "border-orange-700/50 bg-orange-600/5" : "border-stone-800"}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 text-stone-500 text-[10px] uppercase tracking-widest">
        <Icon size={12} /> {label}
      </div>
      <div className={`font-display font-bold text-3xl mt-2 ${accent ? "text-orange-400" : "text-stone-100"}`}>{value}</div>
    </div>
  );
}

function DashboardReelCard({ reel }) {
  const isReady = (reel.status === "done" || reel.status === "ready") && reel.stream_url;
  return (
    <div className="border border-stone-800" data-testid={`dashboard-reel-${reel.id}`}>
      <div className="aspect-video bg-black relative">
        {isReady ? (
          <video src={reel.stream_url} controls preload="metadata" className="w-full h-full" />
        ) : reel.status === "failed" ? (
          <div className="w-full h-full flex flex-col items-center justify-center text-rose-400 text-sm">
            <X size={20} /> Failed
          </div>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-stone-400 text-sm">
            <RefreshCw size={20} className="animate-spin mb-2" />
            {reel.status} · {reel.progress || 0}%
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="font-display uppercase font-semibold truncate">{reel.title}</div>
        <div className="flex items-center gap-3 text-[11px] text-stone-500 mt-1">
          <span className="inline-flex items-center gap-1"><Eye size={11} className="text-orange-400" /><span className="text-orange-400 font-mono-tc text-sm">{reel.views}</span> views</span>
          <span className="inline-flex items-center gap-1"><Film size={11} />{reel.clip_count} clip{reel.clip_count !== 1 ? "s" : ""}</span>
        </div>
        {isReady && (
          <a href={reel.stream_url} download className="btn-secondary inline-flex items-center gap-1.5 text-xs mt-2">
            <Download size={11} /> Download
          </a>
        )}
      </div>
    </div>
  );
}
